import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusCell } from "@/components/StatusCell";
import { RiskBadge } from "@/components/RiskBadge";
import WorkerDocuments from "@/components/WorkerDocuments";
import { INTENT_CONFIG, formatDate, relativeDays, CHANNEL_LABEL } from "@/lib/format";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Phone, Mail, MapPin, Briefcase, Building2, CalendarClock, User, Send, PhoneCall, ThumbsUp, HelpCircle, XCircle, Clock, FileText, Pencil, Check, X, Trash2, ArrowRightLeft, ExternalLink, UserX } from "lucide-react";
import { formatRut, isValidRut } from "@/lib/rut";
import { normalizeClPhone, isValidClPhone, CL_PREFIX, normalizeClWhatsapp, isValidClWhatsapp, WA_PREFIX } from "@/lib/phone";
import ReassignDialog from "@/components/ReassignDialog";

const WORKER_FIELDS = ["rut", "first_name", "last_name", "email", "phone", "whatsapp", "city",
  "position", "company", "mandante", "contrato", "project", "assigned_user", "expected_hire_date", "due_date", "whatsapp_opt_in", "visa_temporaria"];

const TL_ICON = { import: Clock, created: User, reminder: Send, status_change: ThumbsUp, response: HelpCircle, manual_contact: PhoneCall, document: FileText };

export default function WorkerSheet({ workerId, open, onClose, canEdit, onChanged }) {
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mandantes, setMandantes] = useState([]);
  const [jornadas, setJornadas] = useState([]);

  const load = () => {
    if (!workerId) return;
    api.get(`/workers/${workerId}`).then((r) => setW(r.data)).catch(() => {});
    api.get(`/workers/${workerId}/jornadas`).then((r) => setJornadas(r.data)).catch(() => setJornadas([]));
  };
  useEffect(() => { if (open) load(); }, [workerId, open]);
  useEffect(() => { if (open) api.get("/mandantes").then((r) => setMandantes(r.data)).catch(() => {}); }, [open]);

  const contratosFor = (mandanteName) => (mandantes.find((m) => m.name === mandanteName)?.contratos) || [];

  const updateStatus = async (code, status) => {
    await api.patch(`/workers/${workerId}/requirements/${code}`, { status });
    toast.success("Estado actualizado"); load(); onChanged && onChanged();
  };
  const manualContact = async (chan) => {
    await api.post(`/workers/${workerId}/manual-contact`, { channel: chan, note: "" });
    toast.success("Contacto registrado"); load(); onChanged && onChanged();
  };
  const simulateResponse = async (resp) => {
    await api.post(`/workers/${workerId}/response`, { response: resp });
    toast.success("Respuesta registrada"); load(); onChanged && onChanged();
  };

  const saveField = async (field, value) => {
    const payload = {};
    WORKER_FIELDS.forEach((k) => (payload[k] = w[k] ?? ""));
    payload.whatsapp_opt_in = w.whatsapp_opt_in ?? true;
    payload.visa_temporaria = w.visa_temporaria ?? false;
    payload[field] = value;
    try {
      await api.put(`/workers/${workerId}`, payload);
      toast.success("Datos actualizados"); load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
      throw e;
    }
  };

  const saveFields = async (patch) => {
    const payload = {};
    WORKER_FIELDS.forEach((k) => (payload[k] = w[k] ?? ""));
    payload.whatsapp_opt_in = w.whatsapp_opt_in ?? true;
    payload.visa_temporaria = w.visa_temporaria ?? false;
    Object.assign(payload, patch);
    try {
      await api.put(`/workers/${workerId}`, payload);
      toast.success("Datos actualizados"); load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
      throw e;
    }
  };

  const deleteWorker = async () => {
    setDeleting(true);
    try {
      await api.delete(`/workers/${workerId}`);
      toast.success("Trabajador eliminado");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally {
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  const intent = INTENT_CONFIG[w?.intent_status] || INTENT_CONFIG.NONE;
  const timeline = [...(w?.timeline || [])].reverse();

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-xl overflow-y-auto p-0">
        <SheetTitle className="sr-only">Ficha del trabajador</SheetTitle>
        {w && (
          <>
            <SheetHeader className="p-6 pr-14 border-b border-slate-100 bg-slate-50">
              <div className="flex items-start gap-4">
                <Avatar className="h-14 w-14"><AvatarFallback className="bg-blue-600 text-white font-semibold">{(w.first_name[0] || "") + (w.last_name[0] || "")}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <SheetTitle className="font-display text-xl truncate">{w.first_name} {w.last_name}</SheetTitle>
                  <p className="text-sm text-slate-500 truncate">{[w.position, (w.mandante || w.company)].filter(Boolean).join(" · ") || "—"}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <RiskBadge level={w.risk_level} />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${intent.cls}`}>{intent.label}</span>
                    {!(w.mandante || w.company) && (
                      <span data-testid="sheet-sin-asignacion-badge"
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                        <UserX size={11} /> SIN ASIGNACIÓN
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-3xl font-display font-bold text-blue-600 leading-none">{w.progress_percentage}%</p>
                  <p className="text-xs text-slate-400 mt-0.5">avance</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <button data-testid="sheet-ficha-completa" onClick={() => {
                  const mName = w.mandante || w.company || "";
                  if (mName) {
                    const m = mandantes.find((x) => x.name === mName);
                    const c = (m?.contratos || []).find((x) => x.name === w.contrato);
                    if (m && c) { nav(`/acreditacion/${m.id}/contratos/${c.id}/trabajadores/${workerId}`); onClose(); return; }
                  }
                  nav(`/trabajadores/${workerId}/ficha`); onClose();
                }}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100">
                  <ExternalLink size={13} /> Ficha completa
                </button>
                {canEdit && (
                  <>
                    <button data-testid="sheet-reassign-worker" onClick={() => setReassignOpen(true)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100">
                      <ArrowRightLeft size={13} /> Reasignar
                    </button>
                    <button data-testid="sheet-delete-worker" onClick={() => setConfirmDel(true)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-100">
                      <Trash2 size={13} /> Eliminar
                    </button>
                  </>
                )}
              </div>
            </SheetHeader>

            <Tabs defaultValue="resumen" className="p-6">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="resumen" data-testid="sheet-tab-resumen">Resumen</TabsTrigger>
                <TabsTrigger value="documentos" data-testid="sheet-tab-documentos">Documentos</TabsTrigger>
              </TabsList>

              <TabsContent value="resumen" className="mt-5 space-y-6">
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      <InfoRow icon={User} label="RUT" value={w.rut} canEdit={canEdit}
                        kind="rut" onSave={(v) => saveField("rut", v)} />
                      <InfoRow icon={Briefcase} label="Cargo" value={w.position} />
                      <AssignmentRow canEdit={canEdit} worker={w} mandantes={mandantes}
                        contratos={contratosFor(w.mandante || w.company || "")}
                        onSave={async (mandante, contrato) => {
                          const patch = { mandante };
                          if (contrato !== undefined) patch.contrato = contrato;
                          try { await saveFields(patch); } catch (_) {}
                        }} />
                      <InfoRow icon={MapPin} label="Ciudad" value={w.city} />
                      <InfoRow icon={Phone} label="Teléfono" value={w.phone} canEdit={canEdit}
                        kind="phone" onSave={(v) => saveField("phone", v)} />
                      <InfoRow icon={Send} label="WhatsApp" value={w.whatsapp} canEdit={canEdit}
                        kind="whatsapp" onSave={(v) => saveField("whatsapp", v)} />
                      <InfoRow icon={Mail} label="Email" value={w.email} />
                      <InfoRow icon={CalendarClock} label="Fecha ingreso" value={formatDate(w.expected_hire_date)} />
                      <InfoRow icon={CalendarClock} label="Fecha límite" value={formatDate(w.due_date)} />
                      <InfoRow icon={User} label="Responsable" value={w.assigned_user} />
                      <InfoRow icon={Send} label="Últ. contacto" value={`${relativeDays(w.last_contact_at)}${w.last_contact_channel ? " · " + (CHANNEL_LABEL[w.last_contact_channel] || w.last_contact_channel) : ""}`} />
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="font-display font-semibold text-slate-800 mb-3">Requisitos de cumplimiento</h3>
                  <div className="space-y-2">
                    {w.requirements.map((r) => (
                      <div key={r.code} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                        <span className="text-sm text-slate-700">{r.name}</span>
                        <StatusCell testid={`sheet-status-${r.code}`} status={r.status} readOnly={!canEdit} onChange={(s) => updateStatus(r.code, s)} />
                      </div>
                    ))}
                  </div>
                </div>

                {jornadas.length > 0 && (
                  <div data-testid="worker-jornadas">
                    <h3 className="font-display font-semibold text-slate-800 mb-3">Jornadas excepcionales aplicables</h3>
                    <div className="space-y-2">
                      {jornadas.map((j) => {
                        const chip = { SOLICITADA: "bg-slate-100 text-slate-700", EN_TRAMITE: "bg-amber-50 text-amber-700",
                                       AUTORIZADA: "bg-emerald-50 text-emerald-700", RECHAZADA: "bg-red-50 text-red-700" }[j.estado] || "bg-slate-100 text-slate-700";
                        const label = { SOLICITADA: "Solicitada", EN_TRAMITE: "En trámite", AUTORIZADA: "Autorizada", RECHAZADA: "Rechazada" }[j.estado] || j.estado;
                        const fmt = (iso) => { if (!iso) return "—"; const s = String(iso).slice(0,10); const [y,m,d]=s.split("-"); return y&&m&&d?`${d}-${m}-${y}`:s; };
                        const dt = Number(j.dias_trabajo || 0), dd = Number(j.dias_descanso || 0);
                        return (
                          <div key={j.id} data-testid={`worker-jornada-${j.id}`} className="p-3 rounded-lg bg-indigo-50/40 border border-indigo-100">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-800">Res. {j.numero_resolucion || "s/n"}</span>
                              <div className="flex items-center gap-1.5">
                                {dt + dd > 0 && (
                                  <span className="inline-flex items-center h-6 px-2 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">{dt}×{dd}</span>
                                )}
                                <span className={`inline-flex items-center h-6 px-2 rounded text-xs font-medium ${chip}`}>{label}</span>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500">Vigencia: {fmt(j.fecha_inicio)} → {fmt(j.fecha_termino)}</p>
                            {(j.fecha_ingreso_ciclo_personal || j.fecha_inicio_ciclo) && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Entra al ciclo: <strong>{fmt(j.fecha_ingreso_ciclo_personal || j.fecha_inicio_ciclo)}</strong>
                                {j.asignado_directo && <span className="ml-1 text-[10px] text-emerald-700 font-semibold">(asignado directamente)</span>}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {canEdit && (
                  <div>
                    <h3 className="font-display font-semibold text-slate-800 mb-2">Registrar contacto manual</h3>
                    <div className="flex gap-2">
                      <ActionBtn onClick={() => manualContact("call")} icon={PhoneCall} label="Llamada" testid="manual-contact-call" />
                      <ActionBtn onClick={() => manualContact("whatsapp")} icon={Send} label="WhatsApp" testid="manual-contact-whatsapp" />
                      <ActionBtn onClick={() => manualContact("email")} icon={Mail} label="Email" testid="manual-contact-email" />
                    </div>
                    <h3 className="font-display font-semibold text-slate-800 mb-2 mt-4">Simular respuesta del trabajador</h3>
                    <div className="flex gap-2">
                      <ActionBtn onClick={() => simulateResponse("CONTINUE")} icon={ThumbsUp} label="Continuar" testid="sim-continue" />
                      <ActionBtn onClick={() => simulateResponse("NEEDS_HELP")} icon={HelpCircle} label="Ayuda" testid="sim-help" />
                      <ActionBtn onClick={() => simulateResponse("WITHDRAW")} icon={XCircle} label="No sigue" testid="sim-withdraw" />
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-display font-semibold text-slate-800 mb-3">Timeline</h3>
                  <div className="space-y-3">
                    {timeline.map((t, i) => {
                      const Icon = TL_ICON[t.type] || Clock;
                      return (
                        <div key={i} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className="h-7 w-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><Icon size={14} /></div>
                            {i < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                          </div>
                          <div className="pb-1"><p className="text-sm text-slate-700">{t.text}</p><p className="text-xs text-slate-400">{formatDate(t.at)}</p></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="documentos" className="mt-5">
                <WorkerDocuments worker={w} canEdit={canEdit} onChanged={() => { load(); onChanged && onChanged(); }} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar {w?.first_name} {w?.last_name}</AlertDialogTitle>
            <AlertDialogDescription>Esta acción es irreversible. Se eliminarán sus documentos, requisitos, alertas y timeline.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="sheet-delete-confirm" disabled={deleting} onClick={deleteWorker} className="bg-red-600 hover:bg-red-700">
              {deleting ? "Eliminando…" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReassignDialog open={reassignOpen} onClose={() => setReassignOpen(false)} worker={w}
        onDone={() => { load(); onChanged && onChanged(); }} />
    </Sheet>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="leading-tight min-w-0"><p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p><p className="text-slate-700 truncate">{value || "—"}</p></div>
    </div>
  );
}

/* -------- Table-row variants (used in resumen) -------- */
function InfoRow({ icon: Icon, label, value, canEdit, kind, onSave }) {
  const editable = !!onSave;
  if (!editable) {
    return (
      <tr className="hover:bg-slate-50/50">
        <td className="w-[180px] py-2 px-4 bg-slate-50/60">
          <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
            <Icon size={13} className="text-slate-400" /> {label}
          </div>
        </td>
        <td className="py-2 px-4 text-sm text-slate-700">{value || <span className="text-slate-400">—</span>}</td>
      </tr>
    );
  }
  return <EditableRow icon={Icon} label={label} value={value} canEdit={canEdit} kind={kind} onSave={onSave} />;
}

function EditableRow({ icon: Icon, label, value, canEdit, kind, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setVal(value || ""); }, [value, editing]);

  const validate = (v) => {
    if (!v) return "";
    if (kind === "rut") return isValidRut(v) ? "" : "RUT inválido";
    if (kind === "phone") return v === CL_PREFIX ? "" : (isValidClPhone(v) ? "" : "Teléfono inválido");
    if (kind === "whatsapp") return v === WA_PREFIX ? "" : (isValidClWhatsapp(v) ? "" : "WhatsApp inválido (+569 + 8 dígitos)");
    return "";
  };
  const onChange = (e) => {
    const raw = e.target.value;
    if (kind === "rut") setVal(formatRut(raw));
    else if (kind === "phone") setVal(normalizeClPhone(raw));
    else if (kind === "whatsapp") setVal(normalizeClWhatsapp(raw));
    else setVal(raw);
  };
  const err = validate(val);
  const commit = async () => {
    if (err) { toast.error(err); return; }
    let toSend = val;
    if (kind === "rut" && val) toSend = formatRut(val);
    else if (kind === "phone" && val === CL_PREFIX) toSend = "";
    else if (kind === "whatsapp" && val === WA_PREFIX) toSend = "";
    if (toSend === (value || "")) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(toSend); setEditing(false); }
    catch { toast.error("No se pudo guardar"); }
    finally { setBusy(false); }
  };

  return (
    <tr className="group hover:bg-slate-50/50">
      <td className="w-[180px] py-2 px-4 bg-slate-50/60">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          <Icon size={13} className="text-slate-400" /> {label}
        </div>
      </td>
      <td className="py-2 px-4">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input autoFocus data-testid={`edit-${label.toLowerCase()}-input`} value={val} onChange={onChange}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              className={`flex-1 h-7 px-2 rounded border text-sm outline-none ${err ? "border-red-400" : "border-slate-300 focus:border-blue-400"}`} />
            <button data-testid={`edit-${label.toLowerCase()}-save`} disabled={busy || !!err} onClick={commit} className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40"><Check size={14} /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
            {err && <p className="text-[10px] text-red-500 ml-1">{err}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-slate-700 truncate">{value || <span className="text-slate-400">—</span>}</span>
            {canEdit && (
              <button data-testid={`edit-${label.toLowerCase()}-btn`}
                onClick={() => { setVal(value || (kind === "phone" ? CL_PREFIX : kind === "whatsapp" ? WA_PREFIX : "")); setEditing(true); }}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition shrink-0" title={`Editar ${label}`}>
                <Pencil size={13} />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function AssignmentRow({ canEdit, worker, mandantes, contratos, onSave }) {
  const [editing, setEditing] = useState(false);
  const [mSel, setMSel] = useState(worker.mandante || worker.company || "");
  const [cSel, setCSel] = useState(worker.contrato || "");
  const [busy, setBusy] = useState(false);
  const currentContratos = (mandantes.find((m) => m.name === mSel)?.contratos) || contratos;
  useEffect(() => {
    if (!editing) { setMSel(worker.mandante || worker.company || ""); setCSel(worker.contrato || ""); }
  }, [worker.mandante, worker.company, worker.contrato, editing]);

  const commit = async () => {
    setBusy(true);
    try { await onSave(mSel, cSel); setEditing(false); }
    catch { toast.error("No se pudo guardar"); }
    finally { setBusy(false); }
  };

  const hasAssignment = !!(worker.mandante || worker.company);

  return (
    <tr className="group hover:bg-slate-50/50">
      <td className="w-[180px] py-2 px-4 bg-slate-50/60">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          <Building2 size={13} className="text-slate-400" /> Mandante / Contrato
        </div>
      </td>
      <td className="py-2 px-4">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <select data-testid="edit-mandante-select" value={mSel} onChange={(e) => { setMSel(e.target.value); setCSel(""); }}
              className="h-7 px-2 rounded border border-slate-300 focus:border-blue-400 text-sm bg-white outline-none min-w-[120px]">
              <option value="">—</option>
              {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
            <select data-testid="edit-contrato-select" value={cSel} onChange={(e) => setCSel(e.target.value)} disabled={!mSel}
              className="h-7 px-2 rounded border border-slate-300 focus:border-blue-400 text-sm bg-white outline-none disabled:opacity-50 min-w-[120px]">
              <option value="">—</option>
              {currentContratos.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <button data-testid="edit-mandante-save" disabled={busy} onClick={commit} className="text-emerald-600 hover:text-emerald-700"><Check size={14} /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            {hasAssignment ? (
              <span className="text-sm text-slate-700 truncate">
                {worker.mandante || worker.company}
                {worker.contrato && <span className="text-slate-400"> · {worker.contrato}</span>}
              </span>
            ) : (
              <span className="text-sm text-slate-400 italic">Sin asignación</span>
            )}
            {canEdit && (
              <button data-testid="edit-mandante-btn" onClick={() => setEditing(true)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition shrink-0" title="Editar mandante y contrato">
                <Pencil size={13} />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function AssignmentEditor({ canEdit, worker, mandantes, contratos, onSave }) {
  const [editing, setEditing] = useState(false);
  const [mSel, setMSel] = useState(worker.mandante || worker.company || "");
  const [cSel, setCSel] = useState(worker.contrato || "");
  const [busy, setBusy] = useState(false);
  const currentContratos = (mandantes.find((m) => m.name === mSel)?.contratos) || contratos;
  useEffect(() => {
    if (!editing) { setMSel(worker.mandante || worker.company || ""); setCSel(worker.contrato || ""); }
  }, [worker.mandante, worker.company, worker.contrato, editing]);

  const commit = async () => {
    setBusy(true);
    try { await onSave(mSel, cSel); setEditing(false); }
    catch (e) { toast.error("No se pudo guardar"); }
    finally { setBusy(false); }
  };

  if (!editing) {
    const hasAssignment = !!(worker.mandante || worker.company);
    const label = hasAssignment ? (worker.mandante || worker.company) : "Sin asignación";
    const sub = hasAssignment && worker.contrato ? ` · ${worker.contrato}` : "";
    return (
      <div className="flex items-start gap-2 group col-span-2">
        <Building2 size={15} className="text-slate-400 mt-0.5 shrink-0" />
        <div className="leading-tight min-w-0 flex-1">
          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Mandante / Contrato</p>
          <p className={`truncate ${hasAssignment ? "text-slate-700" : "text-slate-400 italic"}`}>{label}<span className="text-slate-400 not-italic">{sub}</span></p>
        </div>
        {canEdit && (
          <button data-testid="edit-mandante-btn" onClick={() => setEditing(true)}
            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition" title="Editar mandante y contrato">
            <Pencil size={13} />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 col-span-2">
      <Building2 size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="leading-tight min-w-0 flex-1 grid grid-cols-2 gap-2">
        <div>
          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Mandante</p>
          <select data-testid="edit-mandante-select" value={mSel} onChange={(e) => { setMSel(e.target.value); setCSel(""); }}
            className="mt-0.5 w-full px-2 py-1 rounded border border-slate-300 focus:border-blue-400 text-sm bg-white outline-none">
            <option value="">—</option>
            {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Contrato</p>
          <select data-testid="edit-contrato-select" value={cSel} onChange={(e) => setCSel(e.target.value)} disabled={!mSel}
            className="mt-0.5 w-full px-2 py-1 rounded border border-slate-300 focus:border-blue-400 text-sm bg-white outline-none disabled:opacity-50">
            <option value="">—</option>
            {currentContratos.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <button data-testid="edit-mandante-save" disabled={busy} onClick={commit} className="text-emerald-600 hover:text-emerald-700 mt-0.5"><Check size={14} /></button>
      <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600 mt-0.5"><X size={14} /></button>
    </div>
  );
}

function EditableInfo({ icon: Icon, label, value, canEdit, kind, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setVal(value || ""); }, [value, editing]);

  const validate = (v) => {
    if (!v) return "";
    if (kind === "rut") return isValidRut(v) ? "" : "RUT inválido";
    if (kind === "phone") return v === CL_PREFIX ? "" : (isValidClPhone(v) ? "" : "Teléfono inválido");
    if (kind === "whatsapp") return v === WA_PREFIX ? "" : (isValidClWhatsapp(v) ? "" : "WhatsApp inválido (+569 + 8 dígitos)");
    return "";
  };
  const onChange = (e) => {
    const raw = e.target.value;
    if (kind === "rut") setVal(formatRut(raw));
    else if (kind === "phone") setVal(normalizeClPhone(raw));
    else if (kind === "whatsapp") setVal(normalizeClWhatsapp(raw));
    else setVal(raw);
  };
  const err = validate(val);
  const commit = async () => {
    if (err) { toast.error(err); return; }
    let toSend = val;
    if (kind === "rut" && val) toSend = formatRut(val);
    else if (kind === "phone" && val === CL_PREFIX) toSend = "";
    else if (kind === "whatsapp" && val === WA_PREFIX) toSend = "";
    if (toSend === (value || "")) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(toSend); setEditing(false); }
    catch (e) { toast.error("No se pudo guardar"); }
    finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-start gap-2 group">
        <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
        <div className="leading-tight min-w-0 flex-1">
          <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
          <p className="text-slate-700 truncate">{value || "—"}</p>
        </div>
        {canEdit && (
          <button data-testid={`edit-${label.toLowerCase()}-btn`} onClick={() => { setVal(value || (kind === "phone" ? CL_PREFIX : kind === "whatsapp" ? WA_PREFIX : "")); setEditing(true); }}
            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition" title={`Editar ${label}`}>
            <Pencil size={13} />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="leading-tight min-w-0 flex-1">
        <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
        <input autoFocus data-testid={`edit-${label.toLowerCase()}-input`} value={val} onChange={onChange}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className={`mt-0.5 w-full px-2 py-1 rounded border text-sm outline-none ${err ? "border-red-400" : "border-slate-300 focus:border-blue-400"}`} />
        {err && <p className="text-[10px] text-red-500 mt-0.5">{err}</p>}
      </div>
      <button data-testid={`edit-${label.toLowerCase()}-save`} disabled={busy || !!err} onClick={commit} className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40 mt-0.5"><Check size={14} /></button>
      <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600 mt-0.5"><X size={14} /></button>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium transition-colors">
      <Icon size={14} /> {label}
    </button>
  );
}
